import { Pencil, Trash2, Mail, MailX } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import type { EditableUser } from './types';

type UserListProps = {
  users: EditableUser[];
  onEdit: (user: EditableUser) => void;
  onDelete: (user: EditableUser) => void;
  onToggleNotifications: (user: EditableUser, enabled: boolean) => void;
  canEdit?: (user: EditableUser) => boolean;
  canDelete?: (user: EditableUser) => boolean;
};

export const UserList = ({
  users,
  onEdit,
  onDelete,
  onToggleNotifications,
  canEdit,
  canDelete,
}: UserListProps) => {
  if (users.length === 0) {
    return (
      <div className="py-8 text-center">
        <p className="text-muted-foreground">No users available yet.</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead className="text-center">Notifications</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id}>
              <TableCell className="font-medium">{user.name}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell>
                <span className="bg-primary/10 inline-flex rounded-full px-2 py-0.5 text-xs font-medium">
                  {user.role}
                </span>
              </TableCell>
              <TableCell className="text-center">
                <div className="flex items-center justify-center gap-2">
                  <Switch
                    checked={user.receive_notifications ?? true}
                    onCheckedChange={(checked) => onToggleNotifications(user, checked)}
                    disabled={!(canEdit ? canEdit(user) : true)}
                  />
                  {user.receive_notifications !== false ? (
                    <Mail className="text-muted-foreground h-4 w-4" />
                  ) : (
                    <MailX className="text-muted-foreground h-4 w-4 opacity-50" />
                  )}
                </div>
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-2">
                  {(canEdit ? canEdit(user) : true) && (
                    <Button variant="outline" size="sm" onClick={() => onEdit(user)}>
                      <Pencil className="h-4 w-4" />
                      Edit
                    </Button>
                  )}
                  {(canDelete ? canDelete(user) : user.role !== 'admin') && (
                    <Button variant="destructive" size="sm" onClick={() => onDelete(user)}>
                      <Trash2 className="h-4 w-4" />
                      Delete
                    </Button>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
